import { Link } from "react-router-dom";
import { formatDate } from "@/lib/api";

// Compact article card
export function ArticleCardSmall({ article, testId }) {
  return (
    <Link to={`/article/${article.slug}`} className="group flex gap-3 items-start hover-zoom" data-testid={testId || "article-card-small"}>
      {article.featured_image ? (
        <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100">
          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : <div className="w-24 h-24 rounded-xl bg-slate-100 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        {article.categories?.[0] && <div className="overline text-[9px] text-sky-700 mb-1">{article.categories[0].replace(/-/g, " ")}</div>}
        <h4 className="font-editorial text-[15px] font-semibold text-slate-900 leading-snug title-link line-clamp-3">{article.title}</h4>
        <div className="text-[11px] text-slate-500 mt-1">{formatDate(article.published_at)}</div>
      </div>
    </Link>
  );
}

// Standard grid card
export function ArticleCard({ article, tone = "blue", testId }) {
  const toneCls = tone === "green" ? "tag-green" : "tag-blue";
  return (
    <Link to={`/article/${article.slug}`} className="block card-soft overflow-hidden group hover-zoom" data-testid={testId || "article-card"}>
      {article.featured_image && (
        <div className="aspect-[16/10] overflow-hidden bg-slate-100">
          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2.5">
          {article.categories?.[0] && (
            <span className={`overline text-[9px] px-2.5 py-1 rounded-full ${toneCls}`}>{article.categories[0].replace(/-/g, " ")}</span>
          )}
        </div>
        <h3 className="font-editorial text-xl font-bold text-slate-900 leading-snug title-link line-clamp-3">{article.title}</h3>
        {article.subtitle && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{article.subtitle}</p>}
        <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
          <span>{article.author_name}</span>
          <span>·</span>
          <span>{formatDate(article.published_at)}</span>
        </div>
      </div>
    </Link>
  );
}

// Hero big card
export function ArticleCardHero({ article, compact = false }) {
  const aspect = compact ? "aspect-[16/8] md:aspect-[21/9]" : "aspect-[16/10] md:aspect-[16/9]";
  return (
    <Link to={`/article/${article.slug}`} className="block group hover-zoom" data-testid="article-hero-card">
      <div className={`relative rounded-3xl overflow-hidden ${aspect} bg-slate-900`}>
        {article.featured_image && (
          <img src={article.featured_image} alt={article.title} className="absolute inset-0 w-full h-full object-cover opacity-90" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/40 to-transparent" />
        <div className={`absolute bottom-0 left-0 right-0 ${compact ? "p-5 md:p-7" : "p-6 md:p-10"} text-white`}>
          <div className="flex gap-2 mb-3">
            {article.is_breaking && <span className="overline text-[10px] px-2.5 py-1 rounded-full bg-red-500 text-white">Breaking</span>}
            {article.categories?.[0] && <span className="overline text-[10px] px-2.5 py-1 rounded-full bg-white/90 text-sky-800">{article.categories[0].replace(/-/g, " ")}</span>}
          </div>
          <h2 className={`font-editorial ${compact ? "text-xl md:text-3xl lg:text-4xl" : "text-2xl md:text-4xl lg:text-5xl"} font-bold leading-tight max-w-4xl`}>{article.title}</h2>
          {!compact && article.subtitle && <p className="text-white/80 mt-3 max-w-2xl text-sm md:text-base line-clamp-2">{article.subtitle}</p>}
          <div className="mt-3 text-xs text-white/70">{article.author_name} · {formatDate(article.published_at)}</div>
        </div>
      </div>
    </Link>
  );
}

export function SectionHeader({ eyebrow, title, viewAllTo }) {
  return (
    <div className="flex items-end justify-between mb-6" data-testid={`section-${eyebrow?.toLowerCase().replace(/\s+/g, "-")}`}>
      <div>
        <div className="overline text-sky-700 mb-1.5">{eyebrow}</div>
        <h2 className="font-editorial text-2xl md:text-3xl font-bold text-slate-900">{title}</h2>
      </div>
      {viewAllTo && <Link to={viewAllTo} className="text-sm text-sky-700 hover:text-sky-900 font-semibold whitespace-nowrap">View all →</Link>}
    </div>
  );
}
